import {RichMessageValidator} from "./rich-message";
import * as t from "io-ts";
import {nullableWhenOmitted} from "../web/validation";

const nullableString = t.union([t.string, t.null]);
const nullableNumber = t.union([t.number, t.null]);
const nullableBoolean = t.union([t.boolean, t.null]);
const traceString = nullableWhenOmitted(t.string);
const traceNumber = nullableWhenOmitted(t.number);

export const TicketCostInputValueTransportValidator = t.partial({
  valueType: nullableString,
  numberValue: nullableNumber,
  textValue: nullableString,
  booleanValue: nullableBoolean,
  dttmValue: nullableNumber,
  refId: nullableString,
  unitOfMeasure: nullableString,
  unitOfMeasureType: nullableString,
});

export const TicketCostInputDecisionTransportValidator = t.partial({
  costInputDefId: nullableString,
  objectCostInputValueId: nullableString,
  rateId: nullableString,
  rateName: nullableString,
  rateDetailId: nullableString,
  rateDetailDescription: nullableString,
  sourceType: nullableString,
  valueType: nullableString,
  unitOfMeasure: nullableString,
  unitOfMeasureType: nullableString,
  systemValue: t.union([TicketCostInputValueTransportValidator, t.null]),
  userValue: t.union([TicketCostInputValueTransportValidator, t.null]),
  appliedValue: t.union([TicketCostInputValueTransportValidator, t.null]),
  systemCost: nullableNumber,
  candidateCost: nullableNumber,
  appliedCost: nullableNumber,
  decision: nullableString,
  decisionReason: nullableString,
  reasonCode: nullableString,
  reason: nullableString,
});

export const TicketCostInputsTransportValidator = t.partial({
  inputs: t.union([
    t.record(t.string, t.union([TicketCostInputDecisionTransportValidator, t.null])),
    t.null,
  ]),
});

/**
 * Cost transport used by the ticket-cost inspector. Preview calculations are not persisted, so
 * identifiers and timestamps are intentionally optional while the calculated fields stay typed.
 */
export const TicketCostOcrmTransportValidator = t.partial({
  id: nullableString,
  objectId: nullableString,
  objectType: nullableString,
  rateModelId: nullableString,
  rateModelName: nullableString,
  rateModelType: nullableString,
  rateModelCompanyId: nullableString,
  rateModelCompanyName: nullableString,
  rateModelCompanyType: nullableString,
  rateId: nullableString,
  rateName: nullableString,
  rateCostComponentType: nullableString,
  rateDetailId: nullableString,
  rateDetailDescription: nullableString,
  rateDetailCostComponentType: nullableString,
  rateDetailTimeRate: nullableNumber,
  rateDetailFreightRate: nullableNumber,
  rateDetailLoadRate: nullableNumber,
  costComponentType: nullableString,
  invoiceAccountLookupId: nullableString,
  taxInvoiceAccountLookupId: nullableString,
  billableHours: nullableNumber,
  billableQty: nullableNumber,
  billableFreightQty: nullableNumber,
  timeRate: nullableNumber,
  freightRate: nullableNumber,
  loadRate: nullableNumber,
  taxRate: nullableNumber,
  timeCost: nullableNumber,
  freightCost: nullableNumber,
  loadCost: nullableNumber,
  taxCost: nullableNumber,
  taxableAmount: nullableNumber,
  totalCost: nullableNumber,
  personCost: nullableNumber,
  trailerCost: nullableNumber,
  pickupDemurrageCost: nullableNumber,
  deliveryDemurrageCost: nullableNumber,
  siteRestrictionTimeCost: nullableNumber,
  siteRestrictionFreightCost: nullableNumber,
  siteRestrictionLoadCost: nullableNumber,
  baseFuelSurchargeLoadCost: nullableNumber,
  percentFuelSurchargeLoadCost: nullableNumber,
  shiftLoadCost: nullableNumber,
  ticketRejectLoadCost: nullableNumber,
  splitLoadCost: nullableNumber,
  genericFreightCost: nullableNumber,
  genericLoadCost: nullableNumber,
  genericTimeCost: nullableNumber,
  wellId: nullableString,
  wellName: nullableString,
  wellAfe: nullableString,
  wellAllocation: nullableNumber,
  version: nullableNumber,
  historicalCreateDttm: nullableNumber,
  createDttm: nullableNumber,
  lastModifiedDttm: nullableNumber,
  costInputs: t.union([TicketCostInputsTransportValidator, t.null]),
});

export const TicketCostOcrmArrayTransportValidator = t.array(TicketCostOcrmTransportValidator);

/**
 * Legacy AutoBean JSON can include fields whose runtime representation differs from the REST
 * transport (notably enum-like values). Keep every compatible, known OCRM field and discard only
 * incompatible extras so an in-memory ticket can paint immediately while the REST response loads.
 */
export const TicketCostOcrmSeedArrayTransportValidator = new t.Type<
  TicketCostOcrmTransport[],
  unknown,
  unknown
>(
  "TicketCostOcrmSeedArrayTransport",
  TicketCostOcrmArrayTransportValidator.is,
  (input, context) => {
    const records = t.array(t.UnknownRecord).validate(input, context);
    if (records._tag === "Left") return records;

    const sanitized = records.right.map((record) => Object.entries(TicketCostOcrmTransportValidator.props)
      .reduce<Record<string, unknown>>((result, [key, codec]) => {
        if (!(key in record)) return result;
        const decoded = codec.decode(record[key]);
        if (decoded._tag === "Right") result[key] = decoded.right;
        return result;
      }, {}));

    return TicketCostOcrmArrayTransportValidator.validate(sanitized, context);
  },
  (value) => value,
);

const PreviewActivityValidator = t.partial({
  transportTransportAssoc: t.union([
    t.partial({
      id: nullableString,
      objectCostRateModelList: t.union([TicketCostOcrmArrayTransportValidator, t.null]),
    }),
    t.null,
  ]),
});

export const TicketCostPreviewResponseValidator = t.partial({
  activityList: t.union([t.array(PreviewActivityValidator), t.null]),
});

export const BillingDecisionTraceValidator = t.intersection([t.partial({entityNames: t.record(t.string, t.string)}), t.type({
  evaluationId: t.string,
  version: t.number,
  effectiveDttm: traceNumber,
  inputs: t.record(t.string, t.string),
  steps: t.array(t.intersection([t.partial({message: t.union([RichMessageValidator, t.null])}), t.type({
    code: t.string,
    outcome: t.string,
    entityId: traceString,
    explanation: t.string,
    values: t.record(t.string, t.string),
  })])),
  intervals: t.array(t.type({
    wellId: traceString,
    eventId: traceString,
    stageId: traceString,
    stageName: traceString,
    startDttm: traceNumber,
    endDttm: traceNumber,
    synthetic: t.boolean,
    outcome: t.string,
  })),
})]);

export const TicketCostBillingLocationTransportValidator = t.partial({
  decisionTrace: t.union([BillingDecisionTraceValidator, t.null]),
  billingLocationId: nullableString,
  billingLocationName: nullableString,
  billingLocationType: nullableString,
  billingAfe: nullableString,
  billingAfePrefix: nullableString,
  billingAfeSuffix: nullableString,
  billingAfeSource: nullableString,
  billingAfeSourceLabel: nullableString,
});

export const TicketCostInvoiceTransportValidator = t.partial({
  id: nullableString,
  invoiceNumber: nullableString,
  status: nullableString,
  cost: nullableNumber,
  createDttm: nullableNumber,
  fromDttm: nullableNumber,
  toDttm: nullableNumber,
  rateModelId: nullableString,
  rateModelName: nullableString,
});

export const TicketCostInvoiceArrayTransportValidator = t.array(TicketCostInvoiceTransportValidator);

export type TicketCostOcrmTransport = t.TypeOf<typeof TicketCostOcrmTransportValidator>;
export type TicketCostPreviewResponse = t.TypeOf<typeof TicketCostPreviewResponseValidator>;
export type TicketCostBillingLocationTransport = t.TypeOf<typeof TicketCostBillingLocationTransportValidator>;
export type TicketCostInvoiceTransport = t.TypeOf<typeof TicketCostInvoiceTransportValidator>;

export type TicketCostDetailsRequest = {
  ticketId: string;
  database?: string;
};

export type TicketCostDetailsTransport = {
  saved: TicketCostOcrmTransport[];
  preview: TicketCostOcrmTransport[];
  billingLocation?: TicketCostBillingLocationTransport;
  previewError?: string;
  billingLocationError?: string;
};
